import { useCallback, useEffect, useState } from "react";
import { Star } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Empty } from "@/components/common";
import { api, errMsg } from "@/lib/api";

export function MostStarredView({ onOpen, onToggleStar }) {
  const [items, setItems] = useState(null);
  const [error, setError] = useState("");
  const load = useCallback(() => {
    setError("");
    api
      .get("/practicals/most-starred")
      .then((response) => setItems(response.data.items || []))
      .catch((requestError) => {
        setItems([]);
        setError(errMsg(requestError));
      });
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  if (!items) {
    return (
      <p className="text-sm text-muted-foreground" data-testid="most-starred-loading">
        Loading…
      </p>
    );
  }

  if (error) {
    return (
      <p className="text-sm text-destructive" data-testid="most-starred-error">
        {error}
      </p>
    );
  }

  if (items.length === 0) {
    return <Empty>No practicals have been starred yet.</Empty>;
  }

  return (
    <ol
      className="divide-y overflow-hidden rounded-xl border bg-card"
      data-testid="most-starred-list"
    >
      {items.map((practical, index) => (
        <li key={practical.id} className="flex items-center gap-3 px-4 py-3">
          <span
            className="w-6 text-center font-display text-lg font-bold text-muted-foreground"
            data-testid={`most-starred-rank-${practical.id}`}
          >
            {index + 1}
          </span>
          <button
            type="button"
            onClick={() => onOpen(practical.id)}
            className="min-w-0 flex-1 text-left"
            data-testid={`most-starred-practical-${practical.id}`}
          >
            <span className="block truncate font-medium">{practical.name}</span>
            <span className="block text-xs text-muted-foreground">
              {[practical.subject, practical.topic].filter(Boolean).join(" · ")}
            </span>
          </button>
          <span
            className="flex shrink-0 items-center gap-1 text-sm font-semibold"
            data-testid={`most-starred-count-${practical.id}`}
          >
            <Star className="h-3.5 w-3.5 fill-amber-400 text-amber-500" />
            {practical.star_count}
          </span>
          <Button
            type="button"
            variant="ghost"
            size="icon"
            className="h-8 w-8 shrink-0"
            onClick={() => onToggleStar(practical, load)}
            aria-label={`${practical.starred ? "Unstar" : "Star"} ${practical.name}`}
            data-testid={`most-starred-toggle-${practical.id}`}
          >
            <Star
              className={`h-4 w-4 ${
                practical.starred ? "fill-amber-400 text-amber-500" : ""
              }`}
            />
          </Button>
        </li>
      ))}
    </ol>
  );
}